import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Play, Gauge, TrendingUp, TrendingDown, Minus, Clock, Target, Plus, Trash2, Brain } from "lucide-react";
import api from "../lib/apiClient";
import { CATEGORY_META, RISK_META } from "../lib/gameData";
import { toast } from "sonner";

export default function Coach() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [minutes, setMinutes] = useState(30);
  const [timePlan, setTimePlan] = useState(null);
  const [review, setReview] = useState(null);
  const [newGoal, setNewGoal] = useState("");

  const load = async () => {
    const [c, g] = await Promise.all([api.get("/coach"), api.get("/career-goals")]);
    setData(c.data);
    setGoals(g.data);
  };
  useEffect(() => { load(); }, []);

  const askTime = async () => {
    const { data } = await api.get(`/coach/time?minutes=${minutes}`);
    setTimePlan(data);
  };
  const loadReview = async () => setReview((await api.get("/coach/weekly-review")).data);
  const addGoal = async () => {
    if (!newGoal.trim()) return;
    await api.post("/career-goals", { title: newGoal, is_primary: goals.length === 0 });
    setNewGoal("");
    toast.success("Objectif ajouté");
    load();
  };
  const delGoal = async (id) => { await api.delete(`/career-goals/${id}`); load(); };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const lvl = data.level_info;
  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><Sparkles className="w-7 h-7 text-amber-400" /> Coach</h1>
        <p className="text-zinc-500 text-sm mt-1">Voici où tu en es, ce qui te bloque et quoi faire maintenant.</p>
      </div>

      {/* État actuel */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card testid="coach-state">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Ton état actuel</span>
          <p className="font-display text-4xl font-extrabold gold-text mt-2">{data.artist_score}<span className="text-lg text-zinc-500">/1000</span></p>
          <p className="text-sm text-zinc-400 mt-1">{data.career_title} · Niv. {lvl.level}</p>
          <p className={`text-xs mt-2 font-mono ${data.week_delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>{data.week_delta >= 0 ? "+" : ""}{data.week_delta} pts cette semaine</p>
        </Card>
        <Card className="sm:col-span-2" testid="coach-goal">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Ton principal objectif</span>
          {data.primary_project ? (
            <>
              <p className="font-display text-lg font-bold mt-2">{data.primary_project.title}</p>
              <div className="flex items-center gap-3 mt-2 text-sm text-zinc-400">
                {data.primary_project.phase && <span className="font-mono text-amber-500/80">{data.primary_project.phase}</span>}
                {data.primary_project.risk && <span style={{ color: RISK_META[data.primary_project.risk].color }}>{RISK_META[data.primary_project.risk].dot} {RISK_META[data.primary_project.risk].label}</span>}
              </div>
              <div className="mt-3 h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full gold-gradient" style={{ width: `${data.primary_project.progress}%` }} /></div>
              <p className="text-xs text-zinc-500 mt-1">{data.primary_project.progress}%</p>
            </>
          ) : <p className="text-sm text-zinc-500 mt-2">Aucun projet actif. Crée un objectif ou demande un plan à l'Assistant.</p>}
        </Card>
      </div>

      {/* Diagnostic */}
      <Card testid="coach-diagnostic">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Diagnostic</span>
        <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{data.diagnostic}</p>
      </Card>

      {/* Que dois-je faire maintenant */}
      <Card testid="coach-actions">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Que dois-je faire maintenant ?</span>
        {data.actions.length === 0 ? <p className="text-sm text-zinc-500 mt-2">Rien en attente. Crée un plan avec l'Assistant.</p> : (
          <div className="mt-3 space-y-2">
            {data.actions.map((a, i) => (
              <motion.div key={a.task_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 bg-[#0F0F13] border border-zinc-800 rounded-2xl p-3" data-testid={`coach-action-${i}`}>
                <span className="font-display font-extrabold text-lg text-amber-400 w-6 text-center shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{a.title}</p>
                  <p className="text-xs text-zinc-500 mt-0.5 truncate">{a.why}{a.project_title ? ` · ${a.project_title}` : ""}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs">
                    {a.duration_min ? <span className="text-zinc-500">{a.duration_min} min</span> : null}
                    <span className="font-mono text-amber-400">+{a.xp} XP</span>
                    {a.overdue && <span className="text-red-400">en retard</span>}
                  </div>
                </div>
                <button onClick={() => navigate("/tasks")} data-testid={`coach-start-${i}`} className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-amber-500/15 text-amber-400 border border-amber-500/30 text-xs font-medium hover:bg-amber-500/25"><Play className="w-3.5 h-3.5" /> Commencer</button>
              </motion.div>
            ))}
          </div>
        )}
      </Card>

      {/* 6 domaines */}
      <Card testid="coach-domains">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Analyse des 6 domaines</span>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          {data.domains.map((c) => {
            const meta = CATEGORY_META[c.key];
            const Icon = meta.icon;
            return (
              <div key={c.key} className={`bg-[#0F0F13] border rounded-2xl p-3 ${c.priority === "high" ? "border-amber-500/40" : "border-zinc-800"}`} data-testid={`coach-domain-${c.key}`}>
                <div className="flex items-center justify-between"><Icon className="w-4 h-4" style={{ color: meta.color }} /><span className="font-mono text-sm font-semibold">{c.score}</span></div>
                <p className="text-xs text-zinc-400 mt-1">{meta.label}</p>
                {c.priority === "high" && <p className="text-[10px] text-amber-400 mt-1">Priorité</p>}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Comment progresser (reco) + créer plan */}
      {data.recommendation?.actions?.length > 0 && (
        <Card testid="coach-howto">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Comment progresser ?</span>
          <p className="text-sm text-zinc-300 mt-2">{data.recommendation.message}</p>
          <div className="mt-3 space-y-1.5">
            {data.recommendation.actions.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-zinc-900 rounded-xl px-3 py-2">
                <span className="truncate">{a.title}</span><span className="font-mono text-xs text-amber-400 shrink-0">+{a.points} {a.category}</span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate("/assistant", { state: { prefill: data.recommendation.assistant_prompt, autoSend: true } })} data-testid="coach-create-plan"
            className="mt-4 inline-flex items-center gap-2 px-4 h-10 rounded-xl gold-gradient text-black font-semibold hover:opacity-90"><Sparkles className="w-4 h-4" /> Créer mon plan</button>
        </Card>
      )}

      {/* Modes utilitaires */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card testid="coach-time">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500 flex items-center gap-2"><Clock className="w-4 h-4" /> J'ai peu de temps</span>
          <div className="flex items-center gap-2 mt-3">
            <select value={minutes} onChange={(e) => setMinutes(parseInt(e.target.value))} data-testid="coach-time-select" className="h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm outline-none">
              {[15, 30, 45, 60].map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
            <button onClick={askTime} data-testid="coach-time-btn" className="h-10 px-4 rounded-lg border border-amber-500/40 text-amber-400 text-sm">Proposer</button>
          </div>
          {timePlan && (
            <div className="mt-3 space-y-1.5">
              {timePlan.actions.length === 0 ? <p className="text-sm text-zinc-500">Aucune tâche ne rentre dans ce créneau.</p> :
                timePlan.actions.map((a) => <div key={a.task_id} className="flex items-center justify-between text-sm bg-zinc-900 rounded-lg px-3 py-2"><span className="truncate">{a.title}</span><span className="text-xs text-zinc-500 shrink-0">{a.duration_min} min · +{a.xp}</span></div>)}
              {timePlan.actions.length > 0 && <p className="text-xs text-amber-400 mt-1 font-mono">XP potentiel : +{timePlan.xp_potential} ({timePlan.used_min} min)</p>}
            </div>
          )}
        </Card>
        <Card testid="coach-review">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Bilan de la semaine</span>
          <button onClick={loadReview} data-testid="coach-review-btn" className="mt-3 h-10 px-4 rounded-lg border border-amber-500/40 text-amber-400 text-sm">Générer</button>
          {review && (
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <Mini label="XP gagné" value={`+${review.xp_gained}`} />
              <Mini label="Tâches finies" value={review.tasks_done} />
              <Mini label="Score de progression" value={`${review.score_delta >= 0 ? "+" : ""}${review.score_delta}`} />
              <Mini label="Streak" value={`🔥 ${review.streak}j`} />
            </div>
          )}
        </Card>
      </div>

      {/* Objectifs de carrière */}
      <Card testid="coach-goals">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500 flex items-center gap-2"><Target className="w-4 h-4" /> Mes objectifs de carrière</span>
        <div className="flex gap-2 mt-3">
          <input value={newGoal} onChange={(e) => setNewGoal(e.target.value)} placeholder="Ex : Lancer mon prochain projet avant décembre" data-testid="coach-goal-input"
            className="flex-1 h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm outline-none focus:border-amber-500" />
          <button onClick={addGoal} data-testid="coach-goal-add" className="h-10 px-3 rounded-lg gold-gradient text-black"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="mt-3 space-y-2">
          {goals.map((g) => (
            <div key={g.id} className="flex items-center gap-2 bg-zinc-900 rounded-xl px-3 py-2" data-testid={`goal-${g.id}`}>
              {g.is_primary && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-black font-bold">Principal</span>}
              <span className="flex-1 text-sm truncate">{g.title}</span>
              <button onClick={() => delGoal(g.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {goals.length === 0 && <p className="text-sm text-zinc-500">Aucun objectif défini pour l'instant.</p>}
        </div>
      </Card>

      <MemoryCard />
    </div>
  );
}

function MemoryCard() {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const load = async () => { try { setItems((await api.get("/ai/memory")).data); } catch {} };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!text.trim()) return;
    await api.post("/ai/memory", { type: "preference", content: text, source: "user" });
    setText(""); toast.success("Mémoire enregistrée"); load();
  };
  const del = async (id) => { await api.delete(`/ai/memory/${id}`); load(); };
  return (
    <Card testid="coach-memory">
      <span className="text-xs font-mono uppercase tracking-wider text-amber-500 flex items-center gap-2"><Brain className="w-4 h-4" /> Ce que le Coach retient de toi</span>
      <p className="text-xs text-zinc-500 mt-1">Ajoute des préférences (ex : « je préfère travailler le soir », « pas de pub payante »). Le Coach en tient compte.</p>
      <div className="flex gap-2 mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ex : je publie surtout sur TikTok" data-testid="memory-input"
          className="flex-1 h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm outline-none focus:border-amber-500" />
        <button onClick={add} data-testid="memory-add" className="h-10 px-3 rounded-lg gold-gradient text-black"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="mt-3 space-y-2">
        {items.length === 0 ? <p className="text-sm text-zinc-500">Rien encore. Le Coach apprend au fil de tes actions.</p> :
          items.map((m) => (
            <div key={m.id} className="flex items-center gap-2 bg-zinc-900 rounded-xl px-3 py-2" data-testid={`memory-${m.id}`}>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">{m.type}</span>
              <span className="flex-1 text-sm truncate">{m.content}</span>
              <button onClick={() => del(m.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
      </div>
    </Card>
  );
}

function Card({ children, className = "", testid }) {
  return <div data-testid={testid} className={`bg-[#18181E] border border-zinc-800 rounded-3xl p-5 ${className}`}>{children}</div>;
}
function Mini({ label, value }) {
  return <div className="bg-zinc-900 rounded-xl p-3"><p className="font-display text-xl font-extrabold">{value}</p><p className="text-[11px] text-zinc-500">{label}</p></div>;
}
